import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Minus at the minimum removes the line. The Lemoh cart design has no separate
   * remove control, so pressing minus when the quantity is at its minimum deletes
   * the line, reusing cart-items-component's removal (row animation + empty-cart
   * transition) rather than clamping the value.
   * @param {Event} event
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();

    const { min, value } = this.getCurrentValues();
    if (value > min) {
      this.updateQuantity(-1);
      return;
    }

    const line = Number(this.refs.quantityInput.dataset.cartLine);
    const cartItems = /** @type {any} */ (this.closest('cart-items-component'));
    if (cartItems && line && typeof cartItems.onLineItemRemove === 'function') {
      cartItems.onLineItemRemove(line);
    } else {
      // Fallback: a 0-quantity update, which cart-items-component maps to removal.
      this.refs.quantityInput.value = '0';
      this.onQuantityChange();
    }
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled.
   * Minus stays enabled at the minimum because it removes the line there.
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = false;
    }
    if (!this.serverDisabledPlus) {
      plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
